import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, GripVertical, Trophy, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePipelinesSettings, type Pipeline, type PipelineStage, type StageType } from '@/hooks/usePipelinesSettings';

interface PipelineStagesManagerProps {
  pipeline: Pipeline;
  open: boolean;
  onClose: () => void;
}

export function PipelineStagesManager({ pipeline, open, onClose }: PipelineStagesManagerProps) {
  const {
    stages,
    fetchStages,
    createStage,
    updateStage,
    deleteStage,
    reorderStages,
    hasStageType
  } = usePipelinesSettings();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState<PipelineStage | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    stage_type: 'normal' as StageType,
    is_active: true
  });
  const [pipelineStages, setPipelineStages] = useState<PipelineStage[]>([]);

  useEffect(() => {
    if (open && pipeline) {
      fetchStages(pipeline.id);
    }
  }, [open, pipeline]);

  useEffect(() => {
    const filteredStages = stages.filter(s => s.pipeline_id === pipeline.id);
    setPipelineStages(filteredStages);
  }, [stages, pipeline.id]);

  const handleOpenAddDialog = (stage?: PipelineStage) => {
    if (stage) {
      setSelectedStage(stage);
      setFormData({
        name: stage.name,
        stage_type: stage.stage_type || 'normal',
        is_active: stage.is_active
      });
    } else {
      setSelectedStage(null);
      setFormData({
        name: '',
        stage_type: 'normal',
        is_active: true
      });
    }
    setIsAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
    setSelectedStage(null);
    setFormData({
      name: '',
      stage_type: 'normal',
      is_active: true
    });
  };

  const canSelectStageType = (type: StageType): boolean => {
    if (type === 'normal') return true;
    return !hasStageType(pipeline.id, type, selectedStage?.id);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedStage) {
      await updateStage(selectedStage.id, {
        name: formData.name,
        stage_type: formData.stage_type,
        is_active: formData.is_active
      });
    } else {
      const maxOrder = Math.max(...pipelineStages.map(s => s.order_index), -1);
      await createStage({
        pipeline_id: pipeline.id,
        name: formData.name,
        stage_type: formData.stage_type,
        order_index: maxOrder + 1,
        is_active: formData.is_active
      });
    }

    handleCloseAddDialog();
  };

  const getStageTypeLabel = (type: StageType) => {
    switch (type) {
      case 'won': return 'Ganho';
      case 'lost': return 'Perdido';
      default: return 'Normal';
    }
  };

  const getStageTypeIcon = (type: StageType) => {
    switch (type) {
      case 'won': return <Trophy className="w-4 h-4 text-green-500" />;
      case 'lost': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return null;
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir este estágio?')) {
      await deleteStage(id);
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;

    const newStages = [...pipelineStages];
    [newStages[index - 1], newStages[index]] = [newStages[index], newStages[index - 1]];
    setPipelineStages(newStages);

    const stageIds = newStages.map(s => s.id);
    await reorderStages(pipeline.id, stageIds);
  };

  const handleMoveDown = async (index: number) => {
    if (index === pipelineStages.length - 1) return;

    const newStages = [...pipelineStages];
    [newStages[index], newStages[index + 1]] = [newStages[index + 1], newStages[index]];
    setPipelineStages(newStages);

    const stageIds = newStages.map(s => s.id);
    await reorderStages(pipeline.id, stageIds);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl w-[calc(100%-2rem)] max-h-[90vh] flex flex-col gap-0 p-0 rounded-2xl bg-white border border-[#E5E7EB] text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA]">
          <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-[#E5E7EB] dark:border-[#3F3F46]/50">
            <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">Estágios - {pipeline.name}</DialogTitle>
          </DialogHeader>

          <div className="flex-shrink-0 px-6 pt-4 pb-3 flex flex-wrap gap-2 justify-between items-center">
            <p className="text-sm text-[#64748B] dark:text-[#A1A1AA]">
              Configure os estágios do pipeline
            </p>
            <Button size="sm" onClick={() => handleOpenAddDialog()} className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200">
              <Plus className="w-4 h-4 mr-2" />
              Novo Estágio
            </Button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-4">
            {pipelineStages.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-[#E5E7EB] rounded-xl dark:border-[#3F3F46]">
                <p className="text-[#64748B] dark:text-[#A1A1AA] mb-4">Nenhum estágio cadastrado</p>
                <Button size="sm" onClick={() => handleOpenAddDialog()} className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200">
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Primeiro Estágio
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {pipelineStages.map((stage, index) => (
                  <div
                    key={stage.id}
                    className={`flex items-center gap-3 border rounded-xl p-3 transition-colors ${
                      stage.stage_type === 'won'
                        ? 'border-green-200 bg-green-50 hover:bg-green-100 dark:border-green-600/50 dark:bg-green-900/20 dark:hover:bg-green-900/30'
                        : stage.stage_type === 'lost'
                        ? 'border-red-200 bg-red-50 hover:bg-red-100 dark:border-red-600/50 dark:bg-red-900/20 dark:hover:bg-red-900/30'
                        : 'border-[#E5E7EB] bg-white hover:bg-[#F8FAFC] dark:border-[#3F3F46] dark:bg-[#27272A]/50 dark:hover:bg-[#3F3F46]/50'
                    }`}
                  >
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-[#64748B] hover:bg-[#F1F5F9] dark:text-[#A1A1AA] dark:hover:bg-[#3F3F46]"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                      >
                        <GripVertical className="w-4 h-4 rotate-180" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-[#64748B] hover:bg-[#F1F5F9] dark:text-[#A1A1AA] dark:hover:bg-[#3F3F46]"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === pipelineStages.length - 1}
                      >
                        <GripVertical className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {getStageTypeIcon(stage.stage_type)}
                        <span className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
                          {index + 1}. {stage.name}
                        </span>
                        {stage.stage_type !== 'normal' && (
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            stage.stage_type === 'won'
                              ? 'bg-green-100 text-green-700 dark:bg-green-600/30 dark:text-green-300'
                              : 'bg-red-100 text-red-700 dark:bg-red-600/30 dark:text-red-300'
                          }`}>
                            {getStageTypeLabel(stage.stage_type)}
                          </span>
                        )}
                        {!stage.is_active && (
                          <span className="text-xs bg-[#F1F5F9] text-[#64748B] px-2 py-0.5 rounded dark:bg-[#3F3F46] dark:text-[#A1A1AA]">Inativo</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenAddDialog(stage)}
                        className="text-[#0F172A] hover:bg-[#F1F5F9] dark:text-[#FAFAFA] dark:hover:bg-[#3F3F46]"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(stage.id)}
                        className="text-[#DC2626] dark:text-[#F87171] hover:bg-[#F1F5F9] dark:hover:bg-[#3F3F46] hover:text-[#B91C1C] dark:hover:text-[#F87171]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="flex-shrink-0 px-6 py-4 border-t border-[#E5E7EB] dark:border-[#3F3F46]/50">
            <Button variant="outline" onClick={onClose} className="rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] hover:bg-[#F8FAFC] dark:border-[#3F3F46] dark:bg-transparent dark:text-[#FAFAFA] dark:hover:bg-[#3F3F46]">
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto rounded-2xl bg-white border border-[#E5E7EB] text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA]">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">
              {selectedStage ? 'Editar Estágio' : 'Novo Estágio'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="stage-name" className="text-[#0F172A] dark:text-[#FAFAFA]">Nome *</Label>
              <Input
                id="stage-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Prospecção, Qualificação..."
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]"
                required
              />
            </div>

            <div>
              <Label className="text-[#0F172A] dark:text-[#FAFAFA]">Tipo do Estagio</Label>
              <Select
                value={formData.stage_type}
                onValueChange={(value: StageType) => setFormData({ ...formData, stage_type: value })}
              >
                <SelectTrigger className="w-full h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="won" disabled={!canSelectStageType('won')}>
                    <span className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-green-500" />
                      Ganho
                      {!canSelectStageType('won') && <span className="text-xs text-[#64748B] dark:text-[#A1A1AA]">(ja existe)</span>}
                    </span>
                  </SelectItem>
                  <SelectItem value="lost" disabled={!canSelectStageType('lost')}>
                    <span className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-500" />
                      Perdido
                      {!canSelectStageType('lost') && <span className="text-xs text-[#64748B] dark:text-[#A1A1AA]">(ja existe)</span>}
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-[#64748B] dark:text-[#A1A1AA] mt-1">
                Estagios "Ganho" e "Perdido" arquivam automaticamente as oportunidades
              </p>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="stage-active" className="text-[#0F172A] dark:text-[#FAFAFA]">Ativo</Label>
              <Switch
                id="stage-active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseAddDialog} className="rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] hover:bg-[#F8FAFC] dark:border-[#3F3F46] dark:bg-transparent dark:text-[#FAFAFA] dark:hover:bg-[#3F3F46]">
                Cancelar
              </Button>
              <Button type="submit" className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200">
                {selectedStage ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
